import { jsxs, jsx } from "react/jsx-runtime";
import { CloseIcon } from "@blocksuite/icons";
import { Root, Image, Fallback } from "@radix-ui/react-avatar";
import { a as assignInlineVars } from "./vanilla-extract-dynamic.esm-b0bca947.js";
import clsx from "clsx";
import { useMemo, useRef, useState, forwardRef } from "react";
import { IconButton } from "./button.js";
import { T as Tooltip } from "./tooltip-ccef7f5b.js";
import "./loading.js";
import "@radix-ui/react-tooltip";
const style_css_ts_vanilla = "";
var DefaultAvatarBottomItemStyle = "_183bwcu6";
var DefaultAvatarBottomItemWithAnimationStyle = "_183bwcu7";
var DefaultAvatarContainerStyle = "_183bwcu3";
var DefaultAvatarMiddleItemStyle = "_183bwcu4";
var DefaultAvatarMiddleItemWithAnimationStyle = "_183bwcu5";
var DefaultAvatarTopItemStyle = "_183bwcu8";
var avatarFallback = "_183bwcuc";
var avatarImage = "_183bwcub";
var avatarRoot = "_183bwcu9";
var avatarWrapper = "_183bwcua";
var hoverWrapper = "_183bwcud";
var removeButton = "_183bwcue";
var sizeVar = "var(--_183bwcu0)";
const colorsSchema = [
  ["#FF0000", "#FF00E5", "#FFAE73"],
  ["#FF5C00", "#FFC700", "#FFE073"],
  ["#FFDA16", "#FFFBA6", "#FFBE73"],
  ["#8CD317", "#FCFF5C", "#67CAE9"],
  ["#28E19F", "#89FFC6", "#39A880"],
  ["#35B7E0", "#77FFCE", "#5076FF"],
  ["#3D39FF", "#77BEFF", "#3502FF"],
  ["#BD08EB", "#755FFF", "#6967E4"]
];
const ColorfulFallback = ({ char }) => {
  const colors = useMemo(() => {
    const index = char.toUpperCase().charCodeAt(0);
    return colorsSchema[index % colorsSchema.length];
  }, [char]);
  const timer = useRef();
  const [topColor, middleColor, bottomColor] = colors;
  const [isHover, setIsHover] = useState(false);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: DefaultAvatarContainerStyle,
      onMouseEnter: () => {
        timer.current = setTimeout(() => {
          setIsHover(true);
        }, 300);
      },
      onMouseLeave: () => {
        clearTimeout(timer.current);
        setIsHover(false);
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: DefaultAvatarTopItemStyle,
            style: { background: bottomColor }
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: clsx(DefaultAvatarMiddleItemStyle, {
              [DefaultAvatarMiddleItemWithAnimationStyle]: isHover
            }),
            style: { background: middleColor }
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: clsx(DefaultAvatarBottomItemStyle, {
              [DefaultAvatarBottomItemWithAnimationStyle]: isHover
            }),
            style: { background: topColor }
          }
        )
      ]
    }
  );
};
const Avatar = forwardRef(
  ({
    size = 20,
    style: propsStyles = {},
    url,
    name,
    className,
    colorfulFallback = false,
    hoverIcon,
    fallbackProps: { className: fallbackClassName, ...fallbackProps } = {},
    imageProps,
    avatarProps,
    onRemove,
    hoverWrapperProps: {
      className: hoverWrapperClassName,
      ...hoverWrapperProps
    } = {},
    avatarTooltipOptions,
    removeTooltipOptions,
    removeButtonProps: {
      className: removeButtonClassName,
      ...removeButtonProps
    } = {},
    ...props
  }, ref) => {
    const firstCharOfName = useMemo(() => {
      return name?.slice(0, 1) || "A";
    }, [name]);
    const [imageDom, setImageDom] = useState(null);
    const [removeButtonDom, setRemoveButtonDom] = useState(null);
    return /* @__PURE__ */ jsxs(Root, { className: avatarRoot, ...avatarProps, ref, children: [
      /* @__PURE__ */ jsx(
        Tooltip,
        {
          portalOptions: { container: imageDom },
          ...avatarTooltipOptions,
          children: /* @__PURE__ */ jsxs(
            "div",
            {
              ref: setImageDom,
              className: clsx(avatarWrapper, className),
              style: {
                ...assignInlineVars({
                  [sizeVar]: size ? `${size}px` : "20px"
                }),
                ...propsStyles
              },
              ...props,
              children: [
                /* @__PURE__ */ jsx(
                  Image,
                  {
                    className: avatarImage,
                    src: url || "",
                    alt: name,
                    ...imageProps
                  }
                ),
                /* @__PURE__ */ jsx(
                  Fallback,
                  {
                    className: clsx(avatarFallback, fallbackClassName),
                    delayMs: url ? 600 : void 0,
                    ...fallbackProps,
                    children: colorfulFallback ? /* @__PURE__ */ jsx(ColorfulFallback, { char: firstCharOfName }) : firstCharOfName
                  }
                ),
                hoverIcon ? /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: clsx(hoverWrapper, hoverWrapperClassName),
                    ...hoverWrapperProps,
                    children: hoverIcon
                  }
                ) : null
              ]
            }
          )
        }
      ),
      onRemove ? /* @__PURE__ */ jsx(
        Tooltip,
        {
          portalOptions: { container: removeButtonDom },
          ...removeTooltipOptions,
          children: /* @__PURE__ */ jsx(
            IconButton,
            {
              size: "extraSmall",
              type: "default",
              className: clsx(removeButton, removeButtonClassName),
              onClick: onRemove,
              ref: setRemoveButtonDom,
              ...removeButtonProps,
              children: /* @__PURE__ */ jsx(CloseIcon, {})
            }
          )
        }
      ) : null
    ] });
  }
);
Avatar.displayName = "Avatar";
export {
  Avatar
};
